// Helper condivisi per il collegamento clienti CRM ↔ pazienti AlfaDocs multi-studio.
// Formato nuovo: metadata.alfadocs_links = { [practiceId]: { patient_id, linked_at } }
//               + metadata.alfadocs_primary_practice
// Formato legacy: metadata.alfadocs_patient_id — mantenuto come MIRROR del link
// primario (retrocompatibilità con lettori vecchi, es. build mobile). Il legacy è
// attribuibile a uno studio SOLO conoscendo la connessione più vecchia dell'org.
// Copia speculare lato edge: supabase/functions/_shared/alfadocsLinks.ts

export interface AlfaDocsLink { patient_id: string; linked_at?: string }
export type AlfaDocsLinksMap = Record<string, AlfaDocsLink>

type Meta = Record<string, any> | null | undefined;

export function hasAlfaDocsLink(metadata: Meta): boolean {
  if (!metadata) return false;
  if (metadata.alfadocs_links && Object.keys(metadata.alfadocs_links).length > 0) return true;
  return !!metadata.alfadocs_patient_id;
}

export function getAlfaDocsLinks(metadata: Meta, oldestPracticeId?: string | null): AlfaDocsLinksMap {
  if (!metadata) return {};
  if (metadata.alfadocs_links && Object.keys(metadata.alfadocs_links).length > 0) {
    return metadata.alfadocs_links as AlfaDocsLinksMap;
  }
  if (metadata.alfadocs_patient_id && oldestPracticeId) {
    return { [oldestPracticeId]: { patient_id: String(metadata.alfadocs_patient_id) } };
  }
  return {};
}

export function getPatientIdForPractice(metadata: Meta, practiceId: string, oldestPracticeId?: string | null): string | null {
  const links = getAlfaDocsLinks(metadata, oldestPracticeId);
  return links[practiceId]?.patient_id ?? null;
}

export function getPrimaryPracticeId(metadata: Meta, oldestPracticeId?: string | null): string | null {
  if (!metadata) return null;
  if (metadata.alfadocs_primary_practice) return String(metadata.alfadocs_primary_practice);
  const links = getAlfaDocsLinks(metadata, oldestPracticeId);
  const keys = Object.keys(links);
  return keys.length > 0 ? keys[0] : null;
}

export function buildLinkPatch(
  metadata: Meta,
  practiceId: string,
  patientId: string,
  nowIso: string,
  oldestPracticeId?: string | null,
): Record<string, any> {
  const existing = getAlfaDocsLinks(metadata, oldestPracticeId);
  const links: AlfaDocsLinksMap = { ...existing, [practiceId]: { patient_id: String(patientId), linked_at: nowIso } };
  const patch: Record<string, any> = { alfadocs_links: links, alfadocs_last_sync: nowIso };
  const hadLinks = Object.keys(existing).length > 0;
  if (!hadLinks) {
    // Primo collegamento in assoluto: questa practice diventa primaria e il
    // mirror legacy punta al suo patient_id.
    patch.alfadocs_primary_practice = practiceId;
    patch.alfadocs_patient_id = String(patientId);
  } else if (!metadata?.alfadocs_primary_practice) {
    // Metadata legacy convertito al volo: la primaria è la practice del legacy.
    patch.alfadocs_primary_practice = getPrimaryPracticeId(metadata, oldestPracticeId);
  }
  return patch;
}

export function buildAlfaSourceId(practiceId: string, appointmentId: string | number): string {
  return `alfadocs_${practiceId}_${appointmentId}`;
}

export function parseAlfaSourceId(sourceId: string | null | undefined): { practiceId: string | null; appointmentId: string } | null {
  if (!sourceId || !sourceId.startsWith('alfadocs_')) return null;
  const rest = sourceId.slice('alfadocs_'.length);
  const parts = rest.split('_');
  if (parts.length >= 2) return { practiceId: parts[0], appointmentId: parts.slice(1).join('_') };
  return { practiceId: null, appointmentId: rest };
}

// True se il cliente è collegato allo studio practiceId. I clienti solo-legacy
// (alfadocs_patient_id senza alfadocs_links) appartengono allo studio più
// vecchio dell'org (oldestPracticeId).
export function clientBelongsToStudio(metadata: Meta, practiceId: string, oldestPracticeId?: string | null): boolean {
  const links = getAlfaDocsLinks(metadata, oldestPracticeId);
  return practiceId in links;
}

// PracticeId collegati al cliente, con la practice primaria per prima
// (per il rendering dei badge "Studio" nelle viste tabella).
export function getClientStudioIds(metadata: Meta, oldestPracticeId?: string | null): string[] {
  const links = getAlfaDocsLinks(metadata, oldestPracticeId);
  const ids = Object.keys(links);
  if (ids.length <= 1) return ids;
  const primary = getPrimaryPracticeId(metadata, oldestPracticeId);
  if (primary && ids.includes(primary)) return [primary, ...ids.filter((id) => id !== primary)];
  return ids;
}
